import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { IconButton } from '../../../shared/button/IconButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Bookshelf, Delete, Document, Sort } from '../../../shared/icons';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';
import { Action } from '../../../shared/modal/ActionModal';
import { ArkTable } from '../../../shared/table/ArkTable';
import { ArkTableColumn, ColumnSort, TableFilterType } from '../../../shared/table/types';
import { getAllClients } from '../../clients/store';
import { getAllDocumentNames } from '../../documents/store';
import { AIAgreementType } from '../queries/store';
import { DeleteFewShotDocumentModal } from './DeleteFewShotDocumentModal';
import styles from './FewShotDocuments.module.scss';
import { PriorityModal } from './priority/PriorityModal';
import { clearTableFilters, FewShotDocumentTable, getFewShotDocuments, getFewShotDocumentsTotal, getIsFetchingDocuments, getTableColumnSort, getTableFilters, getTablePageNumber, getTablePageSize, openFewShotDocumentStarted, paginationNext, paginationPrevious, setPageSize, setSelectedAgreementTypeId, setTableColumnSort, setTableFilters, toggleDeleteConfirmationModal, toggleFewShotDocumentsUploadModal, togglePriorityOrderModalOpen } from './store';

interface FewShotDocumentsTableProps {
    agreementTypeId: number;
    aiAgreementTypes: AIAgreementType[];
}

export const FewShotDocumentsTable: React.FC<FewShotDocumentsTableProps> = ({ agreementTypeId, aiAgreementTypes }) => {
    const dispatch = useAppDispatch();

    const isFetchingDocuments = useAppSelector(getIsFetchingDocuments);
    const mlTestDocuments = useAppSelector(getFewShotDocuments);
    const totalDocuments = useAppSelector(getFewShotDocumentsTotal);
    const filters = useAppSelector(getTableFilters);
    const pageNumber = useAppSelector(getTablePageNumber);
    const columnSort = useAppSelector(getTableColumnSort);
    const pageSize = useAppSelector(getTablePageSize);
    const allClients = useAppSelector(getAllClients);
    const allDocumentNames = useAppSelector(getAllDocumentNames);

    const headerLabel = useMemo(() => aiAgreementTypes.find(aiAgreementType => aiAgreementType.agreementTypeId === agreementTypeId)?.agreementType || '', [aiAgreementTypes, agreementTypeId]);

    const nextPage = useCallback(() => dispatch(paginationNext()), [dispatch]);
    const previousPage = useCallback(() => dispatch(paginationPrevious()), [dispatch]);
    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => dispatch(setTableFilters(key, value, type)), [dispatch]);
    const clearAllFilters = useCallback(() => dispatch(clearTableFilters()), [dispatch]);
    const updatePageSize = useCallback((pageSize: number) => dispatch(setPageSize(pageSize)), [dispatch]);
    const toggleColumnSort = useCallback((columnSort: ColumnSort) => dispatch(setTableColumnSort(columnSort)), [dispatch]);

    const documentNameOptions = useMemo(() => allDocumentNames.filter(documentName => documentName.agreementTypeId === agreementTypeId).map(({ documentName }) => ({ value: documentName, label: documentName })), [allDocumentNames, agreementTypeId]);
    const clientNameOptions = useMemo(() => allClients.map(({ clientId, clientName }) => ({ value: clientId!.toString(), label: clientName })), [allClients]);
    const systemOptions = useMemo(() => [{ value: '0', label: 'Non System' }, { value: '1', label: 'System' }], []);

    const openPriorityReorderModal = useCallback(() => dispatch(togglePriorityOrderModalOpen(true)), [dispatch]);

    const openDocument = useCallback(({ mlFewShotDocumentId, location, mimeType }: FewShotDocumentTable) => dispatch(openFewShotDocumentStarted(location, mlFewShotDocumentId, mimeType)), [dispatch]);
    const toggleDeleteModal = useCallback((documentToDelete: FewShotDocumentTable | null) => dispatch(toggleDeleteConfirmationModal(documentToDelete)), [dispatch]);

    const actions = useCallback((data: FewShotDocumentTable): Action[] => [{ label: 'Select', icon: Document, onClick: () => openDocument(data), withSeparator: true }, { label: 'Delete', icon: Delete, onClick: () => toggleDeleteModal(data), isDelete: true }], [openDocument, toggleDeleteModal]);

    const clientNameFormatter = useCallback((id: number) => allClients.find(({ clientId }) => clientId === id)?.clientName || '', [allClients]);

    const columnDefs: ArkTableColumn[] = useMemo(() => [
        { id: 'documentDescription', header: 'Document Description', field: 'documentDescription', width: 0.25, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'documentName', header: 'Document Name', field: 'documentName', width: 0.25, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'clientId', header: 'Client', field: 'clientId', width: 0.2, component: 'tooltip', canFilter: true, canSort: true, valueFormatter: clientNameFormatter },
        { id: 'isSystem', header: 'System', field: 'isSystem', width: 0.1, component: 'tick', canFilter: true, canSort: true },
        { id: 'priority', header: 'Priority', field: 'priority', width: 0.1, component: 'tooltip' },
        { id: 'isConfirmed', header: 'Confirmed', field: 'isConfirmed', width: 0.05, component: 'tick' },
        { id: 'actions', header: '', field: '', width: 0.05, component: 'action', actions: actions }
    ], [actions, clientNameFormatter]);

    const openModal = useCallback(() => dispatch(toggleFewShotDocumentsUploadModal(true)), [dispatch]);
    const backToSelect = useCallback(() => dispatch(setSelectedAgreementTypeId(null)), [dispatch]);

    const filterDropdownOptions = useMemo(() => ({
        documentName: documentNameOptions,
        clientId: clientNameOptions,
        isSystem: systemOptions
    }), [documentNameOptions, clientNameOptions, systemOptions]);

    if (isFetchingDocuments) {
        return <PageLoadingDots />;
    }

    return (
        <div className={styles.fewShotDocumentsWrapper} data-testid='few-shot-documents-table-wrapper'>
            <div className={styles.fewShotDocumentsHeaderWrapper}>
                <div className={styles.fewShotDocumentsHeader}>
                    <div className={styles.fewShotDocumentsLabel}>{headerLabel}</div>
                    <PlusButton onClick={openModal} fontSize={22} />
                </div>
                <div>
                    <IconButton icon={Sort} onClick={openPriorityReorderModal} fontSize={35} overlayText='Re-order Modal' tooltipPlacement='top' />
                    <IconButton icon={Bookshelf} onClick={backToSelect} fontSize={35} overlayText='Agreement Select' tooltipPlacement='top' />
                </div>
            </div>
            <ArkTable
                colDefs={columnDefs}
                rowData={mlTestDocuments}
                testId='system-admin-few-shot-documents'
                isLoading={isFetchingDocuments}
                page={pageNumber}
                total={totalDocuments}
                next={nextPage}
                previous={previousPage}
                filters={filters}
                updateFilter={updateFilter}
                clearAllFilters={clearAllFilters}
                onRowDoubleClicked={row => openDocument(row as FewShotDocumentTable)}
                columnSort={columnSort}
                toggleSort={toggleColumnSort}
                pageSize={pageSize}
                updatePageSize={updatePageSize}
                removeTextFilter={['clientId', 'isSystem']}
                filterDropdownOptions={filterDropdownOptions}
            />
            <DeleteFewShotDocumentModal closeModal={() => toggleDeleteModal(null)} />
            <PriorityModal />
        </div>
    );
};
